import { useState } from "react";
import {
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { Loyalty as LoyaltyIcon, CardGiftcard as GiftIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  CouponTemplateFormDto,
  ActivationType,
  CouponStatusAfterIssuance,
} from "../../../store/models/coupon/CouponManagementDto";

interface CouponTypeStepProps {
  formData: CouponTemplateFormDto;
  updateFormData: (updates: Partial<CouponTemplateFormDto>) => void;
  readOnly?: boolean;
}

const CouponTypeStep = ({ formData, updateFormData, readOnly = false }: CouponTypeStepProps) => {
  const { t } = useTranslation();
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  const handleFieldTouch = (fieldName: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleActivationTypeChange = (newType: ActivationType) => {
    updateFormData({
      activationType: newType,
      requiredPoints: newType === ActivationType.FREE ? undefined : formData.requiredPoints,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#3859ff" }}>
        {t("coupon.form.couponType")}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Coupon Selection (Free vs Purchaseable) */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              border: formData.activationType === ActivationType.FREE ? "2px solid #3859ff" : "2px solid #e5e7eb",
              backgroundColor: formData.activationType === ActivationType.FREE ? "#f5f7fa" : "white",
              cursor: readOnly ? "default" : "pointer",
              transition: "all 0.2s",
              "&:hover": {
                boxShadow: readOnly ? 0 : 3,
              },
            }}
            onClick={() => !readOnly && handleActivationTypeChange(ActivationType.FREE)}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.activationType === ActivationType.FREE}
                      onChange={() => handleActivationTypeChange(ActivationType.FREE)}
                      sx={{ color: "#3859ff" }}
                      disabled={readOnly}
                    />
                  }
                  label=""
                  sx={{ mr: 1 }}
                />
                <GiftIcon sx={{ fontSize: 32, color: "#3859ff", mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("coupon.form.freeCoupon")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                {t("coupon.form.freeCouponDescription")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              border: formData.activationType === ActivationType.POINTS ? "2px solid #3859ff" : "2px solid #e5e7eb",
              backgroundColor: formData.activationType === ActivationType.POINTS ? "#f5f7fa" : "white",
              cursor: readOnly ? "default" : "pointer",
              transition: "all 0.2s",
              "&:hover": {
                boxShadow: readOnly ? 0 : 3,
              },
            }}
            onClick={() => !readOnly && handleActivationTypeChange(ActivationType.POINTS)}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={formData.activationType === ActivationType.POINTS}
                      onChange={() => handleActivationTypeChange(ActivationType.POINTS)}
                      sx={{ color: "#3859ff" }}
                      disabled={readOnly}
                    />
                  }
                  label=""
                  sx={{ mr: 1 }}
                />
                <LoyaltyIcon sx={{ fontSize: 32, color: "#gray.500", mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("coupon.form.purchaseableCoupon")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                {t("coupon.form.purchaseableCouponDescription")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Dynamic Loyalty Points Input */}
        {formData.activationType === ActivationType.POINTS && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, pt: 4, borderTop: "1px solid #e5e7eb" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("coupon.form.purchaseRequirements")}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t("coupon.form.loyaltyPointsRequired")}
                    value={formData.requiredPoints || ""}
                    onChange={(e) => updateFormData({ requiredPoints: Number.parseInt(e.target.value) || undefined })}
                    onBlur={() => handleFieldTouch("requiredPoints")}
                    placeholder="e.g., 500 points"
                    sx={{ backgroundColor: "white" }}
                    required
                    error={touchedFields.requiredPoints && (!formData.requiredPoints || formData.requiredPoints <= 0)}
                    helperText={
                      touchedFields.requiredPoints && (!formData.requiredPoints || formData.requiredPoints <= 0)
                        ? t("coupon.form.errors.requiredPointsRequired")
                        : ""
                    }
                    inputProps={{ min: 1 }}
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Initial Activation State */}
        <Grid item xs={12}>
          <Box sx={{ pt: 4, borderTop: "1px solid #e5e7eb" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              {t("coupon.form.initialActivationState")}
            </Typography>
            <RadioGroup
              row
              value={formData.initialCouponStatus}
              onChange={(e) => updateFormData({ initialCouponStatus: e.target.value as CouponStatusAfterIssuance })}
              sx={{ gap: 4 }}
            >
              <FormControlLabel
                value={CouponStatusAfterIssuance.ACTIVE}
                control={<Radio sx={{ color: "#3859ff" }} disabled={readOnly} />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {t("coupon.statusAfterIssuance.active")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("coupon.form.activeStateDescription")}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={CouponStatusAfterIssuance.INACTIVE}
                control={<Radio sx={{ color: "#3859ff" }} disabled={readOnly} />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {t("coupon.statusAfterIssuance.inactive")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("coupon.form.inactiveStateDescription")}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CouponTypeStep;
